import React, { createContext, useContext, useState, useEffect } from 'react';
import { rebrandProduct, rebrandStoredProducts } from '../utils/brandText';

const ComparisonContext = createContext();

export const useComparison = () => {
  const context = useContext(ComparisonContext);
  if (!context) {
    throw new Error('useComparison must be used within a ComparisonProvider');
  }
  return context;
};

export const ComparisonProvider = ({ children }) => {
  const [comparisonList, setComparisonList] = useState([]);
  const [isCustomerLoggedIn, setIsCustomerLoggedIn] = useState(false);

  useEffect(() => {
    // Load comparison list from localStorage
    const savedComparison = localStorage.getItem('mineralsComparison');
    if (savedComparison) {
      try {
        setComparisonList(rebrandStoredProducts(JSON.parse(savedComparison)));
      } catch (error) {
        console.error('Error loading comparison from localStorage:', error);
      }
    }

    // Check authentication status
    const userRole = localStorage.getItem('userRole');
    const isCustomer = userRole === 'customer';
    setIsCustomerLoggedIn(isCustomer);

    // Listen for authentication changes
    const handleCustomStorageChange = () => {
      const userRole = localStorage.getItem('userRole');
      const isCustomer = userRole === 'customer';
      setIsCustomerLoggedIn(isCustomer);
    };

    window.addEventListener('localStorageChange', handleCustomStorageChange);

    return () => {
      window.removeEventListener('localStorageChange', handleCustomStorageChange);
    };
  }, []);

  const addToComparison = (product) => {
    // Limit comparison to 4 products max
    if (comparisonList.length >= 4) {
      alert('You can compare up to 4 products at a time. Please remove some items first.');
      return;
    }

    // Check if product is already in comparison
    if (comparisonList.find(item => item.id === product.id)) {
      alert('This product is already in your comparison list.');
      return;
    }

    const newComparisonList = [...comparisonList, rebrandProduct(product)];
    setComparisonList(newComparisonList);
    localStorage.setItem('mineralsComparison', JSON.stringify(newComparisonList));
  };

  const removeFromComparison = (productId) => {
    const newComparisonList = comparisonList.filter(item => item.id !== productId);
    setComparisonList(newComparisonList);
    localStorage.setItem('mineralsComparison', JSON.stringify(newComparisonList));
  };

  const clearComparison = () => {
    setComparisonList([]);
    localStorage.removeItem('mineralsComparison');
  };

  const isInComparison = (productId) => {
    return comparisonList.find(item => item.id === productId);
  };

  const value = {
    comparisonList,
    addToComparison,
    removeFromComparison,
    clearComparison,
    isInComparison,
    isCustomerLoggedIn
  };

  return (
    <ComparisonContext.Provider value={value}>
      {children}
    </ComparisonContext.Provider>
  );
};
